import { supabase } from "@/lib/supabase";

/**
 * Permission identifiers are database contracts, not role rules. The database
 * decides which roles receive them; the frontend only asks whether the
 * authenticated user currently holds them.
 */
export type PermissionKey = `${string}:${string}`;

export interface ActivePermission {
  permission_id: string;
  permission_name: string;
  resource: string;
  action: string;
}

export async function fetchActivePermissions(roleIds: string[]): Promise<ActivePermission[]> {
  if (roleIds.length === 0) return [];

  const { data, error } = await supabase
    .from("role_permissions")
    .select("permission_id, permissions(name, resource, action)")
    .in("role_id", roleIds);

  if (error) throw error;

  return (data ?? [])
    .map((row) => {
      const permission = row.permissions as unknown as {
        name: string;
        resource: string;
        action: string;
      } | null;

      if (!permission) return null;

      return {
        permission_id: row.permission_id,
        permission_name: permission.name,
        resource: permission.resource,
        action: permission.action,
      };
    })
    .filter((row): row is ActivePermission => row !== null);
}

export function permissionKey(permission: Pick<ActivePermission, "resource" | "action">): PermissionKey {
  return `${permission.resource}:${permission.action}`;
}

export function hasPermission(permissions: ActivePermission[], key: PermissionKey): boolean {
  return permissions.some((permission) => permissionKey(permission) === key);
}

export function hasAnyPermission(
  permissions: ActivePermission[],
  keys: PermissionKey[],
): boolean {
  return keys.some((key) => hasPermission(permissions, key));
}
